import { useState, useCallback } from 'react';
import { facturacionMedicosAPI } from '../services/facturacionMedicosAPI';
import { useAuth } from '../../../core/context/AuthContext';

export const useFacturacionMedicos = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [turnos, setTurnos] = useState([]);
  const [turnosDetalle, setTurnosDetalle] = useState([]);
  const [notificaciones, setNotificaciones] = useState([]);
  const [estadoFacturacion, setEstadoFacturacion] = useState(null);
  const [totales, setTotales] = useState({
    totalPracticas: 0,
    totalPracticasPagas: 0,
    total: 0
  });

  const isAdmin = user?.role === 'Administrador' || user?.role === 'Administrativo' || 
                  user?.roles?.includes('ADMINISTRADOR') || user?.roles?.includes('ADMINISTRATIVO');

  const cargarFacturacion = useCallback(async (idMedico, mes, anio) => {
    setLoading(true);
    try {
      const facturacion = await facturacionMedicosAPI.getFacturacionCompletaMedicos(idMedico, mes, anio);
      setTurnos(facturacion || []);
    } catch (error) {
      console.error('Error al cargar facturación:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const cargarDetalle = useCallback(async (idMedico, mes, anio) => {
    setLoading(true);
    setTurnosDetalle([]);
    setTotales({ totalPracticas: 0, totalPracticasPagas: 0, total: 0 });
    try {
      const fechaDesde = `${anio}-${String(mes).padStart(2, '0')}-01`;
      const fechaHasta = mes === 12 
        ? `${anio + 1}-01-01` 
        : `${anio}-${String(mes + 1).padStart(2, '0')}-01`;

      const [detalle, estado] = await Promise.all([
        facturacionMedicosAPI.getFacturacionMedicos(idMedico, fechaDesde, fechaHasta),
        facturacionMedicosAPI.getEstadoFacturacion(idMedico, fechaDesde, fechaHasta)
      ]);

      setTurnosDetalle(detalle || []);
      
      if (estado && estado.length > 0) {
        const estadoOrdenado = estado.sort((a, b) => (b.idEstadoFacturacionMedico || b.idEstadoFacturacionTecnico) - (a.idEstadoFacturacionMedico || a.idEstadoFacturacionTecnico));
        const estadoConAdjuntos = estadoOrdenado.find(e => e.adjuntoFactura || e.adjuntoComprobante);
        setEstadoFacturacion(estadoConAdjuntos || estadoOrdenado[0]);
      }

      calcularTotales(detalle || []);
    } catch (error) {
      console.error('Error al cargar detalle:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const cargarNotificaciones = useCallback(async (envia, recibe, mes, anio) => {
    try {
      const [notifsIda, notifsVuelta] = await Promise.all([
        facturacionMedicosAPI.getNotificaciones(envia, recibe, mes, anio),
        facturacionMedicosAPI.getNotificaciones(recibe, envia, mes, anio)
      ]);
      const todas = [...(notifsIda || []), ...(notifsVuelta || [])];
      const sinDuplicados = todas.filter((notif, index, self) =>
        index === self.findIndex(n => n.idNotificacion === notif.idNotificacion)
      );
      sinDuplicados.sort((a, b) => new Date(a.fechaCreacion) - new Date(b.fechaCreacion));
      setNotificaciones(sinDuplicados);
    } catch (error) {
      console.error('Error al cargar notificaciones:', error);
    }
  }, []);

  const calcularTotales = (data) => {
    if (!data || data.length === 0) {
      setTotales({ totalPracticas: 0, totalPracticasPagas: 0, total: 0 });
      return;
    }

    if (data[0]?.cantidad !== undefined && data[0]?.importe !== undefined) {
      setTotales({
        totalPracticas: data[0].cantidad || 0,
        totalPracticasPagas: data[0].cantidad || 0,
        total: parseFloat(data[0].importe || 0).toFixed(2)
      });
      return;
    }

    let totalPracticas = 0;
    let totalPracticasPagas = 0;
    let total = 0;

    const estadosQueSuman = ['Realizado', 'Enviado', 'Escoreado / Muestras en carpeta', 'En proceso', 'Solicitado'];

    data.forEach(item => {
      const estado = item.estadosTurno?.estadoTurno || '';
      const importe = parseFloat(item.importe || 0);

      totalPracticas++;

      if (estadosQueSuman.includes(estado)) {
        totalPracticasPagas++;
        total += importe;
      }
    });

    setTotales({
      totalPracticas,
      totalPracticasPagas,
      total: total.toFixed(2)
    });
  };

  const subirFactura = useCallback(async (files, idEstadoFacturacion) => {
    try {
      const formData = new FormData();
      Array.from(files).forEach(file => formData.append('files', file));
      formData.append('idEstadoFacturacion', String(idEstadoFacturacion));
      formData.append('usuario', user?.username || '');

      await facturacionMedicosAPI.uploadFactura(formData);
      return true;
    } catch (error) {
      console.error('Error al subir factura:', error);
      throw error;
    }
  }, [user]);

  const subirComprobante = useCallback(async (files, idEstadoFacturacion) => {
    try {
      const formData = new FormData();
      Array.from(files).forEach(file => formData.append('files', file));
      formData.append('idEstadoFacturacion', String(idEstadoFacturacion));
      formData.append('usuario', user?.username || '');

      await facturacionMedicosAPI.uploadComprobante(formData);
      return true;
    } catch (error) {
      console.error('Error al subir comprobante:', error);
      throw error;
    }
  }, [user]);

  const enviarNotificacion = useCallback(async (mensaje, mes, anio, destinatario) => {
    try {
      await facturacionMedicosAPI.createNotificacion(destinatario, mes, anio, mensaje);
    } catch (error) {
      console.error('Error al enviar notificación:', error);
      throw error;
    }
  }, []);

  return {
    loading,
    turnos,
    turnosDetalle,
    setTurnosDetalle,
    notificaciones,
    estadoFacturacion,
    setEstadoFacturacion,
    totales,
    setTotales,
    isAdmin,
    cargarFacturacion,
    cargarDetalle,
    cargarNotificaciones,
    subirFactura,
    subirComprobante,
    enviarNotificacion
  };
};
